import { useEffect, useState } from "react";
import { Breadcrumbs } from "../../../Home/Breadcrumb"
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEmpresaGestora } from "../../../../hooks/useEmpresaGestora";
import { EmpresaGestora } from "../../../../types/EmpresaGestora";
import { Instalacion } from "../../../../types/Instalacion";
import { TrabajoEmpresa } from "../../../../types/TrabajoEmpresa";
import { AreaEmpresa } from "../../../../types/AreaEmpresa";
import { ToastContainer, toast } from "react-toastify";
import { findAll, uploadFile } from "../../../../services/Service";
import SeccionEmpresaGestora from "./EmpresaGestora";
import SeccionInstalaciones from "./Instalaciones";
import SeccionTiposTrabajo from "./TiposTrabajo";
import SeccionAreas from "./Areas";
import Confirm from "../../../../components/Confirm";


export type FormEmpresaGestoraErrors = {
    idTipoDocPersona?: string;
    nroIdentificacion?: string;
    nombreEmpresa?: string;
    direccion?: string;
    telefono?: string;
    email?: string;
};

export const CrearEmpresaGestora = () => {
    const {
        handleEmpresaGestoraSelected, empresaGestoraInitial, empresaGestoraSelected, guardarEmpresaGestora, buscarEmpresaGestora,
        instalacionList, buscarInstalaciones, handleInstalacionList,
        tiposTrabajoList, buscarTiposTrabajoAsociados, handleTiposTrabajoList,
    } = useEmpresaGestora();

    const [empresaGestoraForm, setEmpresaGestoraForm] = useState<EmpresaGestora>(empresaGestoraInitial);
    const [edit, setEdit] = useState<boolean>(false);
    const [view, setView] = useState<boolean>(false);
    const [instalaciones, setInstalaciones] = useState<Instalacion[]>([]);
    const [tiposTrabajo, setTiposTrabajo] = useState<TrabajoEmpresa[]>([]);
    const [areas, setAreas] = useState<AreaEmpresa[]>([]);
    // Si las áreas asociadas no se pudieron cargar no se envían al guardar,
    // para que el backend no las interprete como eliminadas
    const [areasCargadas, setAreasCargadas] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const toggle = () => setShowModal(!showModal);

    const [formErrors, setFormErrors] = useState<FormEmpresaGestoraErrors>({
        idTipoDocPersona: '',
        nroIdentificacion: '',
        nombreEmpresa: '',
        email: '',
    });

    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (id) {
            buscarEmpresaGestora(id);
            buscarInstalaciones(+id);
            buscarTiposTrabajoAsociados(+id);
            findAll(`areas-empresa/empresaGestora/${id}`)
                .then(response => {
                    setAreas(response?.data || []);
                    setAreasCargadas(true);
                })
                .catch(() => toast.error('No se pudieron cargar las Áreas de la Empresa Gestora'));
            setEdit(true);
            const accionActual = searchParams.get("action");
            if (accionActual === 'view') {
                setView(true);
            }
        } else {
            handleEmpresaGestoraSelected(empresaGestoraInitial)
            handleInstalacionList([]);
            handleTiposTrabajoList([]);
            setAreasCargadas(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setEmpresaGestoraForm(empresaGestoraSelected)
    }, [empresaGestoraSelected])

    useEffect(() => {
        setInstalaciones(instalacionList)
    }, [instalacionList])

    useEffect(() => {
        setTiposTrabajo(tiposTrabajoList)
    }, [tiposTrabajoList])

    const validateForm = (empresaGestora: EmpresaGestora) => {
        const errors: FormEmpresaGestoraErrors = {};
        if (!empresaGestora.idTipoDocPersona) {
            errors.idTipoDocPersona = "El campo Tipo de Identificación es obligatorio"
        }
        if (!empresaGestora.nroIdentificacion) {
            errors.nroIdentificacion = "El campo Número de Identificación es obligatorio"
        }
        if (!empresaGestora.nombreEmpresa) {
            errors.nombreEmpresa = "El campo Razón Social es obligatorio"
        }
        if (!empresaGestora.email) {
            errors.email = "El campo Mail es obligatorio"
        }
        setFormErrors(errors);
        return errors;
    }

    const handleGuardar = () => {
        const errores = validateForm(empresaGestoraForm);
        let esValido = Object.keys(errores).length === 0;

        if (!esValido) {
            Object.values(errores).forEach(mensaje => toast.error(mensaje as string));
        }

        if (instalaciones.length === 0) {
            toast.error('Se debe ingresar al menos una Instalación');
            esValido = false;
        }
        if (tiposTrabajo.length === 0) {
            toast.error('Se debe ingresar al menos un Tipo de Trabajo');
            esValido = false;
        }
        if (areas.length === 0) {
            toast.error('Se debe ingresar al menos un Área');
            esValido = false;
        }

        if (esValido) {
            let logo: File | null = empresaGestoraForm.logoNuevo;

            // Las instalaciones nuevas (no persistidas) se identifican en el
            // estado local con un id temporal negativo; se limpian antes de
            // enviarlas al backend para que las trate como altas nuevas.
            const instalacionesAEnviar = instalaciones.map(inst => ({
                ...inst,
                id: inst.id && inst.id > 0 ? inst.id : undefined,
            }));

            guardarEmpresaGestora({
                ...empresaGestoraForm,
                logo: null,
                instalaciones: instalacionesAEnviar as Instalacion[],
                tiposTrabajo: tiposTrabajo,
                areas: areasCargadas
                    ? areas.map(a => ({ id: a.id, idArea: a.idArea, nombreArea: a.nombreArea, email: a.email, activo: true }))
                    : undefined,
            })
                .then(response => {
                    if (response.data) {
                        if (logo) {
                            uploadFile('empresasGestoras/subirLogo',
                                logo, { idEmpresaGestora: response.data.id, sizeMaximo: 2 }, true)
                                .then(result => {
                                    if (result) {
                                        toast.success("Se guardó la Empresa Gestora correctamente")
                                        navigate('/empresasGestoras');
                                    }
                                }).catch((error) => {
                                    if (error.response?.status === 413) {
                                        toast.error(error.response.data.message)
                                    }
                                });
                        } else {
                            toast.success("Se guardó la Empresa Gestora correctamente")
                            navigate('/empresasGestoras');
                        }
                    }
                })
                .catch(err => {
                    if (err.response?.status === 403) {
                        toast.error('No tiene permisos para realizar esta acción');
                    } else {
                        toast.error(err.response?.data?.message || 'Hubo un error al guardar la Empresa Gestora')
                    }
                })
        }
    }

    const handleCancelar = (e: any) => {
        e.preventDefault();
        setShowModal(true)
    }
    const cancelar = () => {
        toggle();
        navigate('/empresasGestoras')
    }
    const handleVolver = () => {
        navigate('/empresasGestoras')
    }

    return (
        <>
            <ToastContainer />

            <div className="content-page">
                <div className="content">
                    <div className="container-fluid">
                        <Breadcrumbs
                            title={edit ? (view ? 'Vista' : 'Editar') : 'Nuevo'}
                            titlePage={view ? 'Vista Empresas Gestoras' : 'Empresas Gestoras'}
                            nameFirst={'Configuración'}
                            urlFirst={'/'}
                            nameSecond={'Empresas Gestoras'}
                            urlSecond={'/empresasGestoras'}
                        />
                        <Confirm isOpen={showModal} toggle={toggle} title='Cancelar' yesFn={cancelar}>
                            <div className="form-horizontal">
                                <div className="row">
                                    {<p>Confirma Salir sin guardar los cambios?</p>}
                                </div>
                            </div>
                        </Confirm>

                        <SeccionEmpresaGestora
                            empresaGestoraForm={empresaGestoraForm}
                            setFormErrors={setFormErrors}
                            setEmpresaGestoraForm={setEmpresaGestoraForm}
                            errors={formErrors} edit={edit} view={view} />

                        <SeccionInstalaciones
                            instalaciones={instalaciones}
                            setInstalaciones={setInstalaciones}
                            edit={edit}
                            view={view}
                        />

                        <SeccionTiposTrabajo
                            tiposTrabajo={tiposTrabajo}
                            setTiposTrabajo={setTiposTrabajo}
                            idEmpresaGestora={empresaGestoraForm.id}
                            nombreEmpresa={empresaGestoraForm.nombreEmpresa}
                            edit={edit}
                            view={view}
                        />

                        <SeccionAreas
                            areas={areas}
                            setAreas={setAreas}
                            edit={edit}
                            view={view}
                        />

                        <br />
                        <div className="text-center">
                            {!view &&
                                <>
                                    <button className="btn btn-secondary mx-2" onClick={handleCancelar}>Cancelar</button>
                                    <button className="btn btn-success mx-2" onClick={handleGuardar}>Guardar</button>
                                </>
                            }
                            {view &&
                                <button className="btn btn-secondary mx-2" onClick={handleVolver}>Volver</button>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
